import { useImperativeHandle, useMemo } from 'react';
import { type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button/Button';
import { LocaleTabs } from '@/shared/components/ui/LocaleTabs/LocaleTabs';
import { cn } from '@/shared/utils/cn/cn.util';

import { DynamicFormFields } from '../DynamicFormFields/DynamicFormFields';
import { DynamicFormSection } from '../DynamicFormSection/DynamicFormSection';
import {
  FORM_ACTIONS_CLASSES,
  FORM_ERROR_SUMMARY_CLASSES,
  FORM_REGION_CLASSES,
  FORM_ROOT_CLASSES,
} from '../dynamic-form.tokens';
import { isSectionVisible } from '../hidden-fields/hidden-fields';
import { resolveSections } from '../resolve-sections/resolve-sections';

import { type DynamicFormProps } from './DynamicForm.types';
import { useDynamicFormLogic } from './useDynamicFormLogic';

/**
 * A form built from field definitions and validated by a CONTRACT schema.
 *
 * **Fields render in the order they were written, translatable or not** (P10.2d).
 * They used to be partitioned — plain fields in one grid, translatable ones in a
 * second grid inside per-locale tabs — which put a question's own label below
 * twenty structural fields and made "interleave these two" unexpressible. Now
 * the locale strip sits above the whole form and decides which TRANSLATION is on
 * screen; each translatable field says which locale it is holding, in its label
 * as well as in a chip, because it now stands among fields that have no locale
 * at all.
 *
 * The old partition answered one question this must still answer: what happens
 * when the invalid field is in the translation that is NOT on screen. The answer
 * is unchanged and now complete — `onInvalid` switches to the offending locale,
 * marks its tab, and (new here) focuses the field once it exists, which the
 * old code attempted against an unmounted control and silently missed.
 *
 * `noValidate` turns off the browser's own bubbles: they are unstyled, appear in
 * the browser's language rather than the portal's, and would fire before the
 * schema — which is the only authority worth reporting.
 */
export function DynamicForm<TValues extends FieldValues>({
  fields,
  sections,
  schema,
  initialValues,
  onSubmit,
  onCancel,
  submitLabel,
  cancelLabel,
  children,
  defaultLocale,
  locale: controlledLocale,
  onLocaleChange,
  columns,
  className,
  renderLayout,
  formRef,
  onDirtyChange,
  onValuesChange,
}: DynamicFormProps<TValues>) {
  const { t } = useTranslation('uiKit');
  const resolvedSections = useMemo(
    () => resolveSections({ fields, sections, columns }),
    [columns, fields, sections],
  );

  const {
    form,
    locale,
    setLocale,
    invalidLocales,
    invalidSectionIds,
    values,
    formErrors,
    isSubmitting,
    submit,
    submitProgrammatically,
    reset,
    focusFirstProblem,
    pathFor,
    sourcePathFor,
    markSlugManual,
    handleAfterChange,
    reportParseFailure,
  } = useDynamicFormLogic({
    sections: resolvedSections,
    schema,
    initialValues,
    onSubmit,
    defaultLocale,
    // Spread conditionally: `locale: undefined` would be indistinguishable from
    // "controlled, currently nothing", and the hook reads the key's presence.
    ...(controlledLocale === undefined ? {} : { locale: controlledLocale }),
    ...(onLocaleChange === undefined ? {} : { onLocaleChange }),
    ...(onDirtyChange === undefined ? {} : { onDirtyChange }),
    ...(onValuesChange === undefined ? {} : { onValuesChange }),
  });

  useImperativeHandle(
    formRef,
    () => ({ submit: submitProgrammatically, reset, focus: focusFirstProblem }),
    [focusFirstProblem, reset, submitProgrammatically],
  );

  const hasTranslatableField = resolvedSections.some((section) =>
    section.fields.some((field) => field.isTranslatable === true),
  );

  const shared = {
    control: form.control,
    locale,
    values,
    pathFor,
    sourcePathFor,
    onSlugManualEdit: markSlugManual,
    onAfterChange: handleAfterChange,
    onParseFailureChange: reportParseFailure,
  };

  /**
   * The fields region — and the ONE container query container in the form.
   *
   * It must be a real element rather than a fragment (the grids ask it how wide
   * they are), and it must stretch: `container-type: inline-size` takes the
   * contents out of the intrinsic width calculation, so a shrink-to-fit parent
   * would measure it as zero and every column ramp would answer "one column"
   * forever.
   */
  const body = (
    <div
      // The grip a measured walk needs: a grid's column ramp answers to the
      // nearest container, and an object group declares a second one, so
      // "which box did this grid ask?" has to be readable from the DOM.
      data-form-container=""
      className={FORM_REGION_CLASSES}
    >
      {resolvedSections
        .filter((section) => isSectionVisible(section, values))
        .map((section) => (
          <DynamicFormSection
            key={section.id}
            section={section}
            isInvalid={invalidSectionIds.includes(section.id)}
          >
            <DynamicFormFields {...shared} fields={section.fields} columns={section.columns} />
          </DynamicFormSection>
        ))}
    </div>
  );

  const fieldsRegion = (
    <>
      {formErrors.length === 0 ? null : (
        <div role="alert" className={FORM_ERROR_SUMMARY_CLASSES}>
          <p className="font-medium">{t('form.submitFailed')}</p>
          {formErrors.map((message) => (
            <p key={message}>{message}</p>
          ))}
        </div>
      )}

      {hasTranslatableField ? (
        <LocaleTabs
          locale={locale}
          onLocaleChange={setLocale}
          invalidLocales={invalidLocales}
          label={t('locale.contentLanguage')}
        >
          {body}
        </LocaleTabs>
      ) : (
        body
      )}

      {children}
    </>
  );

  const actionsRegion = (
    <>
      {onCancel === undefined ? null : (
        <Button type="button" variant="ghost" disabled={isSubmitting} onClick={onCancel}>
          {cancelLabel ?? t('action.cancel', { ns: 'common' })}
        </Button>
      )}
      <Button type="submit" isLoading={isSubmitting}>
        {submitLabel ?? t('action.save', { ns: 'common' })}
      </Button>
    </>
  );

  return (
    <form noValidate onSubmit={submit} className={cn(FORM_ROOT_CLASSES, className)}>
      {renderLayout === undefined ? (
        <>
          {fieldsRegion}
          <div className={FORM_ACTIONS_CLASSES}>{actionsRegion}</div>
        </>
      ) : (
        renderLayout({ fields: fieldsRegion, actions: actionsRegion })
      )}
    </form>
  );
}
